import React from 'react';
import {
  VStack,
  HStack,
  Box,
  Text,
  Heading,
  Badge,
  Button,
  Tooltip,
  Wrap,
  WrapItem,
} from '@chakra-ui/react';
import { useNavigate } from 'react-router-dom';
import { auth } from '../services/firebase';
import PokerCard from './ui/PokerCard';
import { POKER_COLORS } from '../theme/designSystem';
import { TableData } from '../services/tableService';

//  Special Rules type (matching GameState.ts)

interface SpecialRules {
  sevenTwoGame?: boolean;
  sevenTwoBounty?: number;
  bombPotEnabled?: boolean;
  bombPotAnte?: number;
  straddleEnabled?: boolean;
  straddleMin?: number;
  straddleMax?: number;
  runItTwiceEnabled?: boolean;
  allInOrFold?: boolean;
}

//  Extended TableData with special rules

interface ExtendedTableData extends TableData {
  specialRules?: SpecialRules;
  isPrivate?: boolean;
}

interface PokerTableCardProps {
  table: ExtendedTableData;
  onJoin: (tableId: string) => Promise<void>;
  isJoining?: boolean;
}

//  Helper to get active rule badges

const getActiveRuleBadges = (specialRules?: SpecialRules): { icon: string; label: string; tooltip: string }[] => {
  if (!specialRules) return [];
  
  const badges: { icon: string; label: string; tooltip: string }[] = [];
  
  if (specialRules.sevenTwoGame) {
    badges.push({ 
      icon: '', 

      label: '7-2', 
      tooltip: `7-2 Bounty: Win with 7-2 offsuit for ${specialRules.sevenTwoBounty || 2}x BB bonus!` 
    });
  }
  if (specialRules.bombPotEnabled) {
    badges.push({ 
      icon: '', 

      label: 'Bomb', 
      tooltip: `Bomb Pots enabled! ${specialRules.bombPotAnte || 2}x BB ante, skip preflop` 
    });
  }
  if (specialRules.straddleEnabled) {
    badges.push({ 
      icon: '⬆', 

      label: 'Straddle', 
      tooltip: `Straddle allowed up to ${specialRules.straddleMax || 4}x BB` 
    });
  }
  if (specialRules.runItTwiceEnabled) {
    badges.push({ 
      icon: '', 

      label: 'Run 2x', 
      tooltip: 'Run It Twice available when all-in' 
    });
  }
  if (specialRules.allInOrFold) {
    badges.push({ 
      icon: '', 

      label: 'AoF', 
      tooltip: 'All-In or Fold mode: Go big or go home!' 
    });
  }
  
  return badges;
};

const PokerTableCard: React.FC<PokerTableCardProps> = ({ table, onJoin, isJoining = false }) => {
  const navigate = useNavigate();
  const currentUser = auth.currentUser;

  // Safety checks
  if (!table) return null;

  // Safely extract values with defaults
  const smallBlind = table?.smallBlind ?? 0;
  const bigBlind = table?.bigBlind ?? 0;
  const pot = table?.pot ?? 0;
  const maxSeats = table?.maxSeats ?? 6;
  const players = table?.players ?? [];
  const currency = table?.currency ?? 'playcoins';
  const status = table?.status ?? 'waiting';
  const name = table?.name ?? 'Unnamed Table';
  const specialRules = table?.specialRules;
  const isPrivate = table?.isPrivate ?? false;

  const isPlayerInTable = players.includes(currentUser?.uid || '');
  const isFull = players.length >= maxSeats;
  const isPlaying = status === 'playing';
  
  //  Get special rule badges

  const ruleBadges = getActiveRuleBadges(specialRules);
  const hasSpecialRules = ruleBadges.length > 0;

  const handleClick = async () => {
    if (isPlayerInTable) {
      navigate(`/game/${table.id}`);
    } else if (!isFull) {
      await onJoin(table.id);
    }
  };

  // Determine button text and style
  const getButtonConfig = () => {
    if (isPlayerInTable) {
      return { text: 'Rejoin', bg: '#ff6b35', hoverBg: '#e55a2b' };

    }
    if (isFull) {
      return { text: 'Full', bg: POKER_COLORS.darkBase, hoverBg: POKER_COLORS.darkBase };

    }
    if (isPlaying) {
      return { text: 'Join Game', bg: POKER_COLORS.darkGreen, hoverBg: '#1a8a1a' };

    }
    return { text: 'Join', bg: POKER_COLORS.cyan, hoverBg: POKER_COLORS.accentCyan };

  };

  const buttonConfig = getButtonConfig();

  return (
    <PokerCard 
      p={5} 
      _hover={{ transform: 'translateY(-4px)', boxShadow: 'lg' }} 
      transition="all 0.2s"
      border={isPlaying ? '2px solid' : hasSpecialRules ? '2px solid' : '1px solid'}
      borderColor={isPlaying ? POKER_COLORS.gold : hasSpecialRules ? 'purple.500' : `${POKER_COLORS.cyan}33`}
    >
      <VStack spacing={3} align="stretch">
        {/* Header */}
        <HStack justify="space-between" align="start">
          <VStack align="start" spacing={1} flex={1}>
            <HStack>
              {isPrivate && (
                <Tooltip label="Private Table - Code Required">
                  <Text></Text>

                </Tooltip>
              )}
              <Heading size="sm" color={POKER_COLORS.cyan} isTruncated>
                {name}
              </Heading>
            </HStack>
            <Text color={POKER_COLORS.accentCyan} fontSize="xs">
              ${smallBlind}/${bigBlind}
            </Text>
          </VStack>
          <Badge
            bg={status === 'waiting' ? POKER_COLORS.darkGreen : POKER_COLORS.gold}
            color={status === 'waiting' ? 'white' : POKER_COLORS.darkTeal}
            fontSize="xs"
            px={2}
            py={1}
            borderRadius="md"
            fontWeight="bold"
          >
            {status === 'playing' ? ' LIVE' : 'WAITING'}

          </Badge>
        </HStack>

        {/*  Special Rules Badges */}

        {hasSpecialRules && (
          <Wrap spacing={1}>
            {ruleBadges.map((badge, idx) => (
              <WrapItem key={idx}>
                <Tooltip label={badge.tooltip} hasArrow>
                  <Badge
                    bg="purple.600"
                    color="white"
                    fontSize="xs"
                    px={2}
                    py={0.5}
                    borderRadius="full"
                    cursor="help"
                  >
                    {badge.icon} {badge.label}
                  </Badge>
                </Tooltip>
              </WrapItem>
            ))}
          </Wrap>
        )}

        {/* Currency & Pot */}
        <HStack spacing={4} fontSize="xs">
          <Box>
            <Text color={POKER_COLORS.accentCyan}>Currency</Text>
            <Text color={POKER_COLORS.cyan} fontWeight="bold">
              {currency === 'playcoins' ? '' : ''}

            </Text>
          </Box>
          <Box>
            <Text color={POKER_COLORS.accentCyan}>Pot</Text>
            <Text color={POKER_COLORS.gold} fontWeight="bold">
              ${typeof pot === 'number' ? pot.toFixed(0) : '0'}
            </Text>
          </Box>
        </HStack>

        {/* Players Count */}
        <Box bg={POKER_COLORS.darkBase} p={2} borderRadius="md">
          <HStack spacing={2}>
            {[...Array(maxSeats)].map((_, i) => (
              <Box
                key={i}
                w={6}
                h={6}
                borderRadius="full"
                bg={i < players.length ? POKER_COLORS.gold : POKER_COLORS.darkBase}
                border={`1px solid ${POKER_COLORS.cyan}`}
              />
            ))}
          </HStack>
          <Text fontSize="xs" color={POKER_COLORS.accentCyan} mt={2}>
            {players.length}/{maxSeats} seats
          </Text>
        </Box>

        {/* Action Button */}
        <Button
          w="100%"
          size="sm"
          bg={buttonConfig.bg}
          color="white"
          _hover={{
            bg: buttonConfig.hoverBg,
          }}
          isDisabled={isFull && !isPlayerInTable}
          isLoading={isJoining}
          onClick={handleClick}
          fontWeight="bold"
          fontSize="sm"
        >
          {buttonConfig.text}
        </Button>
      </VStack>
    </PokerCard>
  );
};

export default PokerTableCard;